import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';
import { Spin, Typography } from 'antd';
import { useThemeVarsClass, Select, Tooltip } from '@bettercomp/ui-components';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/user/selectors';
import { useSwitchableWorkspaces } from 'hooks/useSwitchableWorkspaces';
import { useWorkspaceSwitch } from 'hooks/useWorkspaceSwitch';
import { createTenancyBroadcastChannel, TenancyBroadcastMessageType } from './constants';
import cn from './styles.module.scss';

/**
 * Workspace switcher for an AMS session (AMS scenario 12).
 *
 * Sibling of LegacyTenancySwitcher rather than a branch inside it: the two answer different
 * questions — "which tenant am I acting as", decided by an admin role, versus "which of my granted
 * workspaces am I in", decided by the grant list. Keeping them apart means the legacy one is deleted
 * as a file when the migration completes rather than untangled from this.
 *
 * The current workspace comes from Redux rather than the token: api/ resolves the `workspaceId`
 * claim to a Company and hydrates `user.Company` from it, so `Company.code` already *is* the bound
 * workspace code. Decoding the JWT here would be a second source for something the store holds.
 */
export const AmsWorkspaceSwitcher = () => {
  const user = useSelector(selectUser);
  const { workspaces, isLoading, isUnavailable } = useSwitchableWorkspaces();
  const { switchWorkspace, isSwitching } = useWorkspaceSwitch();
  const themeClassName = useThemeVarsClass();

  const [isReloading, setIsReloading] = useState(false);

  useEffect(() => {
    const channel = createTenancyBroadcastChannel();
    const handleMessage = (message: { data: TenancyBroadcastMessageType }) => {
      if (message.data === TenancyBroadcastMessageType.TenancySwitched) {
        window.location.reload();
      }
    };

    channel.addEventListener('message', handleMessage);
    return () => {
      channel.removeEventListener('message', handleMessage);
      channel.close();
    };
  }, []);

  const currentWorkspaceCode = user?.Company?.code;

  const options = useMemo(
    () =>
      workspaces
        .filter((workspace) => workspace.code !== currentWorkspaceCode)
        .map((workspace) => ({ label: workspace.name, value: workspace.code })),
    [workspaces, currentWorkspaceCode],
  );

  const handleChange = async (workspaceCode: string) => {
    const result = await switchWorkspace(workspaceCode);
    if (result !== 'switched') {
      return;
    }

    // Reload rather than invalidate: the workspace-scoped surface in app/ is large enough that any
    // hand-maintained list of caches to clear would rot. Held true through the reload so the control
    // cannot be driven twice while the page tears down.
    setIsReloading(true);
    createTenancyBroadcastChannel().postMessage(TenancyBroadcastMessageType.TenancySwitched);
    window.location.reload();
  };

  // A failed fetch must not read as "you have one workspace" — that would silently remove the
  // control from someone who has several, with nothing on the page saying why.
  if (isUnavailable) {
    return (
      <div className={cn.wrapper}>
        <Tooltip title="Your workspaces could not be loaded. Refresh to try again.">
          <Typography.Text type="secondary" data-testid="workspaceSwitcherUnavailable">
            Workspaces unavailable
          </Typography.Text>
        </Tooltip>
      </div>
    );
  }

  // The card's rule: the control exists only when there is somewhere to go. One workspace is the
  // common single-workspace account, where a picker with nothing in it is noise.
  if (isLoading || workspaces.length <= 1) {
    return null;
  }

  const isBusy = isSwitching || isReloading;

  return (
    <>
      <div className={cn.wrapper}>
        <Select
          placeholder={user?.Company?.name}
          value={undefined}
          options={options}
          onChange={handleChange}
          showSearch={{ optionFilterProp: 'label' }}
          variant="filled"
          size="medium"
          className={cn.tenantSelect}
          classNames={{ popup: { root: cn.tenantSelectPopup } }}
          disabled={isBusy}
          data-testid="workspaceSelector"
        />
      </div>
      {isBusy &&
        createPortal(
          <div className={classnames(themeClassName, cn.switchOverlay)} data-testid="workspaceSwitchOverlay">
            <Spin size="large" />
          </div>,
          document.body,
        )}
    </>
  );
};
